"use client";

/**
 * Prep discovery rail (PR-6). Surfaces the two product features the
 * 2026-07-15 value-delivery audit found EXIST-but-unmeasured — the daily
 * micro-lesson and the free mock interview — on the two surfaces where they
 * were invisible:
 *
 *  - `seeker_dashboard`: the seeker home. The lesson + mock interview only
 *    lived inside the Command-UI `PrepPanel`, which is gated on the
 *    `hunt-redesign` flag (default OFF) → invisible to ~all users.
 *  - `job_detail`: the apex job page. Intent is highest here, so the rail
 *    grounds the mock interview in THIS role (`jobId`/`jobTitle`). Auth is
 *    resolved by the caller via `JobContextProvider` (`useJobContext`) so the
 *    rail renders nothing for anon visitors (no bot/crawler noise in the
 *    `prep_rail_shown` funnel).
 *
 * Reuses the existing `DailyLessonCard` (self-contained, fires its own
 * `daily_lesson_*` events) and `MockInterviewCard` (renders every phase) — no
 * new feature code, just a discovery surface. Telemetry: `prep_rail_shown`
 * (once per authenticated mount) + `prep_rail_feature_clicked {feature}` so
 * PR-7 can measure whether surfacing these features moves engagement.
 *
 * Not user-personalized beyond `lesson`/`jobId` — no new D1 read on the
 * render path (the lesson is already fetched by the seeker page; job_detail
 * passes context the page already has).
 */

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState, useTransition } from "react";
import { DailyLessonCard } from "@/components/hunt/DailyLessonCard";
import { MockInterviewCard } from "@/components/hunt/MockInterviewCard";
import { ZoneLink } from "@/components/layout/zone-link";
import type { Locale } from "@/lib/i18n/config";
import type { DailyLessonView } from "@/lib/learning/resolve-user-track";
import type {
	MockInterviewQuestion,
	MockInterviewState,
} from "@/lib/match/types";
import { generateMockInterview } from "@/server/actions/hunt/mock-interview";

export type PrepRailSurface = "seeker_dashboard" | "job_detail";

type Notice = "no_resume" | "unauthorized" | null;

type Props = {
	locale: Locale;
	surface: PrepRailSurface;
	/** job_detail: grounds the interview in this role. */
	jobId?: string;
	jobTitle?: string;
	/** seeker_dashboard: the user's warmed daily lesson (null until the cron
	 *  has confidently derived the user's track — pass through as-is). */
	lesson?: DailyLessonView | null;
	/** Resolved by the caller. Seeker page is auth-only → always true;
	 *  job_detail reads `useJobContext().isAuthenticated`. When false the rail
	 *  renders nothing and emits no telemetry. */
	isAuthenticated: boolean;
	hasResume?: boolean;
};

export function PrepDiscoveryRail({
	locale,
	surface,
	jobId,
	jobTitle,
	lesson,
	isAuthenticated,
	hasResume,
}: Props) {
	const posthog = usePostHog();
	const isEn = locale === "en";
	const shownRef = useRef(false);
	const [mock, setMock] = useState<MockInterviewState>(() => ({
		phase: "intro",
		role: jobTitle ?? "",
		questions: [],
		focusAdvice: null,
		sessionId: null,
	}));
	const [pending, startTransition] = useTransition();
	const [notice, setNotice] = useState<Notice>(null);

	// Fire the rail impression ONCE per authenticated mount. Gated on auth
	// so the ISR job-detail page never emits for anon visitors / crawlers.
	useEffect(() => {
		if (!isAuthenticated) return;
		if (shownRef.current) return;
		shownRef.current = true;
		posthog?.capture("prep_rail_shown", {
			surface,
			has_resume: hasResume === true,
			has_lesson: !!lesson,
			locale,
		});
	}, [isAuthenticated, surface, hasResume, lesson, locale, posthog]);

	if (!isAuthenticated) return null;

	const onStart = () => {
		setNotice(null);
		setMock((prev) => ({ ...prev, phase: "loading" }));
		posthog?.capture("prep_rail_feature_clicked", {
			surface,
			feature: "mock_interview",
			locale,
		});
		startTransition(async () => {
			const result = await generateMockInterview(locale, jobId);
			if (!result.ok || result.kind !== "mock_interview") {
				if (!result.ok && result.error === "quota_exceeded") {
					setMock((prev) => ({ ...prev, phase: "quota_exceeded" }));
					return;
				}
				if (!result.ok && result.error === "no_resume") {
					setNotice("no_resume");
					setMock((prev) => ({ ...prev, phase: "intro" }));
					return;
				}
				if (!result.ok && result.error === "unauthorized") {
					setNotice("unauthorized");
					setMock((prev) => ({ ...prev, phase: "intro" }));
					return;
				}
				setMock((prev) => ({ ...prev, phase: "error" }));
				return;
			}
			const payload = result.result as {
				questions?: MockInterviewQuestion[];
				focusAdvice?: string;
			};
			const questions = Array.isArray(payload?.questions)
				? payload.questions.slice(0, 6)
				: [];
			setMock((prev) => ({
				...prev,
				phase: questions.length > 0 ? "ready" : "error",
				questions,
				focusAdvice: payload?.focusAdvice ?? null,
				sessionId: result.sessionId || null,
			}));
		});
	};

	return (
		<section
			data-testid="prep-discovery-rail"
			className="space-y-3 rounded-2xl border border-border/60 bg-card/40 p-4"
			aria-label={isEn ? "Prep" : "Persiapan"}
		>
			<div className="flex items-center gap-2">
				<h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
					{isEn ? "Prep" : "Persiapan"}
				</h2>
				{jobTitle ? (
					<span className="line-clamp-1 text-sm text-muted-foreground">
						{isEn ? "for " : "untuk "}
						{jobTitle}
					</span>
				) : null}
			</div>

			{lesson ? (
				<DailyLessonCard lesson={lesson} maxSeq={lesson.seq} locale={locale} />
			) : null}

			{notice === "no_resume" ? (
				<div
					data-testid="prep-rail-no-resume"
					className="space-y-2 rounded-2xl border border-border bg-card p-4"
				>
					<p className="text-sm text-muted-foreground">
						{isEn
							? "Add a resume and we'll tailor the interview questions to your experience."
							: "Unggah resume agar pertanyaan wawancara disesuaikan dengan pengalamanmu."}
					</p>
					<ZoneLink
						href={`/${locale}/resume`}
						className="inline-flex min-h-11 items-center rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn ? "Add resume →" : "Unggah resume →"}
					</ZoneLink>
				</div>
			) : notice === "unauthorized" ? (
				<div
					data-testid="prep-rail-unauthorized"
					className="space-y-2 rounded-2xl border border-border bg-card p-4"
				>
					<p className="text-sm text-muted-foreground">
						{isEn
							? "Sign in to practice interviewing for this role."
							: "Masuk untuk mulai simulasi wawancara untuk posisi ini."}
					</p>
					<ZoneLink
						href={`/${locale}/login?callbackURL=/${locale}/seeker`}
						className="inline-flex min-h-11 items-center rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{isEn ? "Log in →" : "Masuk →"}
					</ZoneLink>
				</div>
			) : (
				<MockInterviewCard
					state={mock}
					locale={locale}
					pending={pending}
					onStart={onStart}
				/>
			)}
		</section>
	);
}
